import React, { FC, useCallback, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'

import { Box, Typography, Link } from '@mui/material'
import { useLocation } from 'react-router'

import { FormRadioGroup, FormTextField, GridRow, BlackRadio, InfoBlock } from '../../Common'
import { DirectorSigningRights, SignatoryRight } from '../../../graphql'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import { APP_PATHS, PATH_PARAMS } from '../../../routes/paths'
import { useCustomHistory, useMatchCurrentLocation } from '../../../hooks'
import { PeopleTabName } from '../../../pages/ContractManagementPage/People/CmPeopleTabs'
import { StyledFormControlLabel } from '../StyledComponents'

interface HandleChange {
  target: { name?: string | undefined; value: number | unknown }
}

export const Authorised: FC<{
  hasControl?: boolean
  signatoryRight?: SignatoryRight | undefined | null
  groupSignature?: number | undefined | null
  fullOptions?: boolean
}> = ({ hasControl = true, signatoryRight, groupSignature, fullOptions }) => {
  const history = useHistory()
  const {
    [PATH_PARAMS.applicationId]: applicationId,
    [PATH_PARAMS.peopleTab]: peopleTab,
  } = useParams() as Record<string, string>
  const { t } = useTranslation()
  const location = useLocation()
  const { watch, setValue } = useFormContext()
  const { addUrl } = useCustomHistory()

  const isDirectorPage = location.pathname.includes('/directors')
  // Checks for contract page add director form
  const isContractPage = useMatchCurrentLocation(APP_PATHS.dashboard.contractManagement.people)
  const isDirectorsTab = peopleTab === PeopleTabName.Directors

  const signatory = watch('signatoryRight')

  const handleNavAuthorizedPersonsStep = useCallback(() => {
    addUrl()
    history.push(generatePath(APP_PATHS.application.authorizedPersons.list, { applicationId }))
  }, [addUrl])

  const handlePeopleTab = useCallback(() => {
    const path = generatePath(APP_PATHS.dashboard.contractManagement.people, {
      applicationId,
      [PATH_PARAMS.peopleTab]: PeopleTabName.Team,
    })

    history.push(path)
  }, [])

  useEffect(() => {
    if (signatoryRight) {
      setValue('signatoryRight', signatoryRight)
    }
    if (groupSignature) {
      setValue('groupSignature', groupSignature)
    }
  }, [signatoryRight, groupSignature, setValue])

  return (
    <Box
      sx={(theme) => ({
        margin: theme.spacing(5, 0, 0, 0),
        [theme.breakpoints.down('xs')]: {
          margin: theme.spacing(4, 0, 0, 0),
        },
      })}
    >
      <Box
        sx={(theme) => ({
          margin: theme.spacing(0, 0, 3, 0),
        })}
      >
        <Typography
          variant={'h2'}
          sx={(theme) => ({
            [theme.breakpoints.down('xs')]: {
              fontSize: '1.25rem',
              lineHeight: '1.75rem',
            },
          })}
        >
          {hasControl
            ? t('signatoryRights', 'Signatory Rights')
            : t('signatoryRightsWithFullAccess)', 'Signatory Rights (with Full Access)')}
        </Typography>

        {isDirectorPage && (
          <GridRow>
            <InfoBlock>
              <Typography>
                {isContractPage && isDirectorsTab ? (
                  <Trans
                    i18nKey="thisSelectionInformationalPurposesInvite"
                    components={{
                      linkNav: (
                        <Link
                          onClick={handlePeopleTab}
                          sx={{
                            color: 'blue',
                            '&:hover': {
                              cursor: 'pointer',
                            },
                          }}
                        />
                      ),
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey="thisSelectionInformationalPurposes"
                    components={{
                      linkNav: (
                        <Link
                          onClick={handleNavAuthorizedPersonsStep}
                          sx={{
                            color: 'blue',
                            '&:hover': {
                              cursor: 'pointer',
                            },
                          }}
                        />
                      ),
                    }}
                  />
                )}
              </Typography>
            </InfoBlock>
          </GridRow>
        )}
      </Box>
      <Box>
        <FormRadioGroup name="signatoryRight" value={signatory || SignatoryRight.Sole}>
          <Box mb={1.75} data-test="signatoryRightSole">
            <StyledFormControlLabel
              value={SignatoryRight.Sole}
              control={<BlackRadio size="small" />}
              label={t('sole', 'Sole')}
            />
          </Box>
          <Box mb={1.75} data-test="signatoryRightDual">
            <StyledFormControlLabel
              value={SignatoryRight.Joint}
              control={<BlackRadio size="small" />}
              label={isDirectorPage ? t('joint', 'Joint') : t('jointDual', 'Joint (Dual)')}
            />
          </Box>
          <Box mb={3} data-test="signatoryRightGroup">
            <StyledFormControlLabel
              value={SignatoryRight.Group}
              control={<BlackRadio size="small" />}
              label={isDirectorPage ? t('group', 'Group') : t('jointGroup', 'Joint (Group)')}
            />
          </Box>
          {fullOptions && (
            <Box mb={3} data-test="signatoryRightNone">
              <StyledFormControlLabel
                value={DirectorSigningRights.NoSigningRights}
                control={<BlackRadio size="small" />}
                label={t('noSigningRights', 'No Signing Rights')}
              />
            </Box>
          )}
        </FormRadioGroup>
      </Box>

      <Box hidden={signatory !== 'group' || isDirectorPage}>
        <GridRow>
          <FormTextField
            label={t('groupSize', 'Group size')}
            name="groupSignature"
            type="number"
            fullWidth={true}
            required={false}
            onChange={useCallback(
              (e: HandleChange) => {
                const { value } = e.target
                setValue('groupSignature', +(value as string).replace(/\D+/g, ''))
              },
              [setValue],
            )}
            data-test="groupSignature"
          />
        </GridRow>
      </Box>
    </Box>
  )
}
